import { useEffect, useRef, useState } from "react";

import { CountryDropdownButton } from "@similarweb/ui-components";

import { translate } from "app/filters/i18nFilter";
import { swSettings } from "app/common/services/swSettings";
import { CountryFilter } from "app/components/filters-bar/country-filter/CountryFilter";
import type { ICountry } from "app/components/filters-bar/country-filter/CountryFilterTypes";
import * as utils from "app/components/filters-bar/utils";
import { benchmarkTrackerTracking } from "app/pages/benchmark-tracker";
import CountryService from "app/services/CountryService";

import { AutomationTagGeneratorBase, generateAutomationName } from "../../../helpers/generateAutomationName";

import { countryDropdownConfig } from "./constants";
import * as SC from "./styles";
import { COMMON_SEARCH_PLACEHOLDER, COUNTRY_DROPDOWN_PLACEHOLDER_TEXT } from "./texts";

type CountryDropdownSchema = {
    onCountrySelect: (countryCode: number) => void;
    isDisabled?: boolean;
    initialSelectedCountry?: number;
};

const CountryDropdown = ({ onCountrySelect, isDisabled, initialSelectedCountry }: CountryDropdownSchema) => {
    const { filters: trackFilter } = benchmarkTrackerTracking;
    const buttonContainerRef = useRef<HTMLDivElement>(null);

    const defaultCountry = swSettings.components?.Activation?.defaultParams.country;

    const initialCountry = CountryService.getCountryById(defaultCountry);
    const initialCountryId = utils.getSelectedId(initialCountry);

    const availableCountries = utils.getCountries(true);

    const [selectedCountry, setSelectedCountry] = useState(initialCountryId);

    const onCountryChange = (country: ICountry) => {
        if (!country) {
            return;
        }

        setSelectedCountry(utils.getSelectedId({ id: country.id }));
        onCountrySelect(country.id);
    };

    const onToggle = () => trackFilter.trackCountryToggle;

    useEffect(() => {
        if (!isDisabled && !initialSelectedCountry) {
            buttonContainerRef.current?.click();
        }
    }, [initialSelectedCountry, isDisabled]);

    return (
        <SC.CountryFilterContainer
            data-automation={generateAutomationName(
                "activation",
                ["setup", "competitors", "ai", "country", "filter"],
                AutomationTagGeneratorBase.Benchmark,
            )}
        >
            <CountryFilter
                key="filter-country"
                availableCountries={availableCountries}
                disabled={isDisabled}
                renderButtonComponent={
                    !initialSelectedCountry
                        ? () => (
                              <SC.CountryButtonContainer ref={buttonContainerRef}>
                                  <CountryDropdownButton disabled={isDisabled}>
                                      {translate(COUNTRY_DROPDOWN_PLACEHOLDER_TEXT)}
                                  </CountryDropdownButton>
                              </SC.CountryButtonContainer>
                          )
                        : null
                }
                changeCountry={onCountryChange as any}
                selectedCountryIds={selectedCountry || {}}
                appendTo="body"
                height={countryDropdownConfig.height}
                width={countryDropdownConfig.width}
                dropdownPopupPlacement="ontop-left"
                onToggle={onToggle}
                searchPlaceHolder={COMMON_SEARCH_PLACEHOLDER}
            />
        </SC.CountryFilterContainer>
    );
};

export default CountryDropdown;
